/**
 * PlayPage — the main game shell: header + nav + active panel + global modals.
 * Redirects to the title screen if there's no live game.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useGameStore } from '@/store/gameStore';
import { useGameLoop } from '@/hooks/useGameLoop';
import { sfx } from '@/game/audio';
import { Confetti } from '@/components/game/art/Scenes';
import {
  BottomNav,
  EventModal,
  GameOverModal,
  HeaderBar,
  AwayModal,
  SideNav,
  type TabId,
} from '@/components/game/Shell';
import { DashboardPanel } from '@/components/game/panels/Dashboard';
import { ProductsPanel } from '@/components/game/panels/Products';
import { SupplyChainPanel } from '@/components/game/panels/Supply';
import { ChannelsPanel } from '@/components/game/panels/Channels';
import { MarketingPanel } from '@/components/game/panels/Marketing';
import { FinancePanel } from '@/components/game/panels/Finance';
import { ResearchPanel } from '@/components/game/panels/Research';
import { NewsPanel } from '@/components/game/panels/News';
import { RanksPanel } from '@/components/game/panels/Ranks';

export default function PlayPage() {
  useSeoMeta({
    title: 'Mattress Empire — Run Your Empire',
    description: 'Dashboard of your mattress empire: cash, KPIs, tariffs, and breaking industry news.',
  });

  const navigate = useNavigate();
  const state = useGameStore((s) => s.state);
  const [tab, setTab] = useState<TabId>('dashboard');

  // ── Milestone celebrations ────────────────────────────────────────────
  // The engine stays pure; the UI watches for achievement unlocks and
  // profitable quarter-closes and throws the party here.
  const [burst, setBurst] = useState(0);
  const prevAch = useRef<number | null>(null);
  const prevQtr = useRef<number | null>(null);

  const achCount = state?.achievements.length ?? 0;
  useEffect(() => {
    if (prevAch.current === null) {
      prevAch.current = achCount;
      return;
    }
    if (achCount > prevAch.current) {
      sfx.play('fanfare');
      setBurst((b) => b + 1);
    }
    prevAch.current = achCount;
  }, [achCount]);

  const qtrCount = state?.history.quarterly.length ?? 0;
  useEffect(() => {
    if (prevQtr.current === null) {
      prevQtr.current = qtrCount;
      return;
    }
    if (qtrCount > prevQtr.current && state) {
      const last = state.history.quarterly[qtrCount - 1];
      if (last && last.netIncome > 0) {
        sfx.play('bell');
        setBurst((b) => b + 1);
      }
    }
    prevQtr.current = qtrCount;
  }, [qtrCount, state]);

  useGameLoop();

  // Unlock WebAudio on the first user gesture (autoplay policy compliance).
  useEffect(() => {
    const unlock = () => sfx.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  useEffect(() => {
    if (!state) navigate('/', { replace: true });
  }, [state, navigate]);

  if (!state) return null;

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar />

      <div className="mx-auto flex max-w-7xl">
        {/* Desktop side rail */}
        <aside className="sticky top-[57px] hidden h-[calc(100vh-57px)] w-56 shrink-0 border-r lg:block">
          <SideNav active={tab} onSelect={setTab} />
        </aside>

        <main className="min-w-0 flex-1 px-3 pb-24 pt-4 sm:px-4 lg:pb-8">
          {tab === 'dashboard' && <DashboardPanel />}
          {tab === 'products' && <ProductsPanel />}
          {tab === 'supply' && <SupplyChainPanel />}
          {tab === 'channels' && <ChannelsPanel />}
          {tab === 'marketing' && <MarketingPanel />}
          {tab === 'finance' && <FinancePanel />}
          {tab === 'research' && <ResearchPanel />}
          {tab === 'news' && <NewsPanel />}
          {tab === 'ranks' && <RanksPanel />}
        </main>
      </div>

      <BottomNav active={tab} onSelect={setTab} />

      {/* Celebrations & global overlays */}
      <Confetti burst={burst} />
      <EventModal />
      <AwayModal />
      <GameOverModal />
    </div>
  );
}
